/************************************** THIRD-PARTY MODULES ***************************************/
import {get} from 'mad-utils/lib/react';
import moment from 'moment';

/**************************************** PROJECT MODULES *****************************************/
import {isMomentProp, setSerializer, setDeserializer} from './utils/serialization-utils';
import {serializationConfig} from './utils/serialization-config';

const {KEEP_ON_LOGOUT, VAL_AFTER_LOGOUT, PROPERTY_TYPE, STORED_PROPS} = serializationConfig.symbols;

const {getMetadata, defineMetadata} = Reflect;

/*************************************** PROPERTY DECORATOR ***************************************/
interface SLConfig<T = any> {
    serializer?: (data: any) => any; //<P = any, R = any>(data: P) => R
    deserializer?: (data: any) => any; //<P = any, R = any>(data: P) => R
    type?: T;
    onLogout?:
        | ({keep: boolean; revertTo?: undefined})
        | ({keep?: undefined; revertTo: Omit<any, undefined>});
    // keepOnLogout?: boolean;
}

/**
 * Shared code for @storeLocally decorator, used by both config & no-config
 * variants
 *
 * Adds key to the array at class-level metadata symbol STORED_PROPS
 *
 * Sets the following metadata symbols on the property itself:
 *     - SERIALIZER
 *     - DESERIALIZER
 *     - PROPERTY_TYPE
 */
function storeLocallyDecorator<T>(config: SLConfig = {}) {
    /**
     * Actual decorator function (constructed by storeLocallyDecorator factory)
     */
    return function storeLocallyDecoratorInner<T extends object>(target: T, key: string): void {
        // Get config vals
        const {serializer, deserializer, onLogout} = config;

        // STORED_PROPS :: Get list of props to store from current class (set to [key] if none)
        const props = (getMetadata(STORED_PROPS, target.constructor) || []).concat(key);

        // Add selected prop to list of props to store, on the current class (constructor)
        defineMetadata(STORED_PROPS, props, target.constructor);

        // SERIALIZER :: Add serializer for current key at current class (if present)
        if (serializer) {
            setSerializer(target, key, serializer);
        } else if (isMomentProp(target.constructor, key)) {
            setSerializer(target, key, (m: moment.Moment) => m.toJSON());
        }

        // DESERIALIZER :: Add deserializer for current key at current class (if present)
        if (deserializer) {
            setDeserializer(target, key, deserializer);
        } else if (isMomentProp(target.constructor, key)) {
            setDeserializer(target, key, moment);
        }

        // PROPERTY_TYPE :: Add property type for current key at current class (if present)
        if (config.type) {
            defineMetadata(PROPERTY_TYPE, config.type, target.constructor, key);
        }

        // PROPERTY_TYPE :: Set to keep current key on logout if given
        //                  storeLocally({onLogout: {keep: true}})
        if (onLogout && typeof onLogout.keep !== `undefined`) {
            Reflect.defineMetadata(KEEP_ON_LOGOUT, onLogout.keep, target.constructor, key);
        }

        // PROPERTY_TYPE :: Set to convert value of current key on logout to given value, if
        //                  given storeLocally({onLogout: {revertTo: `post-login value here`}})
        else if (onLogout && typeof onLogout.revertTo !== `undefined`) {
            Reflect.defineMetadata(VAL_AFTER_LOGOUT, onLogout.revertTo, target.constructor, key);
        }
    };
}

/********************************************* EXPORT *********************************************/
/**
 * Marks property for syncing with localStorage
 *
 * Example:
 *     @storeLocally
 *     @observable
 *     public birthdate: Moment = moment();
 *
 * Example:
 *     @storeLocally
 *     @computed
 *     public get city(): string {
 *         return this.record.cityId;
 *     }
 */
export function storeLocally<T extends object>(target: T, key: string): void;

/**
 * Decorator factory
 * Uses given config object to create decorator for syncing prop w localStorage
 *
 * Example:
 *     @storeLocally({keepOnLogout: true})
 *     @observable
 *     public currentScreen: Int0To2 = 0;
 *
 * @param {object} config Configuration object with props:
 *        - @param {function} serializer Converts data to locally storable form
 *        - @param {function} deserializer Rehydrates locally stored data on retrieval
 *        - @param {any} type Data type
 */
export function storeLocally<T extends object>(config: SLConfig): (target: T, key: string) => void;

//
// [IMPLEMENTATION]
//

export function storeLocally<T extends object>(targetOrConfig?: T, key?: string) {
    const constructor = get(targetOrConfig, `constructor`);
    const hasOwnConstructor = targetOrConfig.hasOwnProperty(`constructor`);

    // Bare decorator
    if (constructor && hasOwnConstructor && typeof constructor === `function`) {
        storeLocallyDecorator({})(targetOrConfig, key);
    }

    // Decorator with config prop
    else {
        return storeLocallyDecorator(targetOrConfig);
    }
}
